<template>
  <section class="financial-bank-accounts">
    <FinancialBankAccountsToolbar
      :text="text"
      :search="search"
      :view-mode="viewMode"
      :status-filter="statusFilter"
      :status-tabs="statusTabs"
      @search-change="handleSearchChange"
      @view-mode-change="handleViewMode"
      @status-change="handleStatusFilter"
    />

    <div v-if="viewMode === 'cards'" class="financial-bank-accounts__grid">
      <FinancialAccountCard
        v-for="account in paginatedAccounts"
        :key="account.id"
        :text="text"
        :account="account"
        :show-menu="true"
        @edit="handleEditAccount"
        @transfer="handleTransferAccount"
        @toggle-status="handleToggleAccountStatus"
        @movements="handleMovements"
        @open-account="handleOpenAccount"
      />

      <article v-if="!paginatedAccounts.length" class="customer-detail-card financial-account-empty">
        <p>{{ text.bankAccounts.empty }}</p>
      </article>
    </div>

    <div v-else class="app-table-wrap financial-bank-accounts__table-wrap">
      <div class="app-table-scroll">
        <table class="app-data-table financial-bank-accounts__table">
          <thead>
            <tr>
              <th>Cuenta</th>
              <th>{{ text.bankAccounts.card.owner }}</th>
              <th>{{ text.bankAccounts.card.branch }}</th>
              <th>{{ text.bankAccounts.card.lastSync }}</th>
              <th>Estado</th>
              <th class="app-text-right">{{ text.summary.labels.available }}</th>
              <th class="app-text-right"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!paginatedAccounts.length">
              <td colspan="7" class="app-table-empty">{{ text.bankAccounts.empty }}</td>
            </tr>

            <tr v-for="account in paginatedAccounts" :key="`table-${account.id}`">
              <td>
                <a
                  class="financial-table-account-cell financial-table-account-link"
                  :href="resolveAccountHref(account)"
                  @click.prevent="handleOpenAccount(account)"
                >
                  <span class="financial-table-account-cell__badge" :class="`is-${account.code}`">
                    {{ account.code.toUpperCase() }}
                  </span>
                  <span>
                    <span class="app-strong">{{ account.name }}</span>
                    <small class="app-muted">{{ account.meta }}</small>
                  </span>
                </a>
              </td>
              <td><span class="financial-bank-accounts__cell-text">{{ account.owner || '' }}</span></td>
              <td><span class="financial-bank-accounts__cell-text">{{ account.branch || '' }}</span></td>
              <td><span class="financial-bank-accounts__cell-text">{{ account.lastSync || '' }}</span></td>
              <td>
                <span class="app-status-pill" :class="account.active ? 'app-status-active' : 'app-status-inactive'">
                  {{ account.statusLabel }}
                </span>
              </td>
              <td class="app-text-right">
                <span v-if="account.active" class="app-strong">{{ account.balance }}</span>
                <span v-else class="app-muted">{{ text.summary.labels.inactiveNoBalance }}</span>
              </td>
              <td class="app-table-actions-cell">
                <div class="app-table-actions-inline">
                  <button
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--view"
                    title="Ver movimientos"
                    aria-label="Ver movimientos"
                    @click="handleMovements(account)"
                  >
                    <i class="fa fa-eye" aria-hidden="true"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="financial-bank-accounts__pagination">
      <AppPagination
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="handlePageChange"
      />
    </div>
  </section>
</template>

<script>
import AppPagination from '@/components/common/AppPagination.vue';
import FinancialBankAccountsToolbar from '@/components/financial/FinancialBankAccountsToolbar.vue';
import FinancialAccountCard from '@/components/financial/cards/FinancialAccountCard.vue';

const BANK_ACCOUNTS_VIEW_MODE_STORAGE_PREFIX = 'orderly.financial.bankAccounts.viewMode';

export default {
  name: 'FinancialBankAccountsTab',
  components: {
    AppPagination,
    FinancialBankAccountsToolbar,
    FinancialAccountCard,
  },
  props: {
    text: {
      type: Object,
      required: true,
    },
    accounts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: '',
      statusFilter: 'active',
      viewMode: 'cards',
      currentPage: 1,
      cardsPerPage: 6,
      tablePerPage: 10,
    };
  },
  mounted() {
    this.restoreViewModeFromStorage();
  },
  computed: {
    perPage() {
      return this.viewMode === 'table' ? this.tablePerPage : this.cardsPerPage;
    },
    statusTabs() {
      return [
        {
          key: 'active',
          label: this.text.bankAccounts.tabs.active,
          count: this.activeCount,
          showCount: true,
        },
        {
          key: 'inactive',
          label: this.text.bankAccounts.tabs.inactive,
          count: this.inactiveCount,
          showCount: true,
        },
      ];
    },
    normalizedAccounts() {
      return Array.isArray(this.accounts) ? this.accounts : [];
    },
    activeCount() {
      return this.normalizedAccounts.filter((account) => Boolean(account?.active)).length;
    },
    inactiveCount() {
      return this.normalizedAccounts.filter((account) => !account?.active).length;
    },
    filteredAccounts() {
      const query = String(this.search || '').trim().toLowerCase();

      return this.normalizedAccounts.filter((account) => {
        const isActiveMatch = this.statusFilter === 'active'
          ? Boolean(account?.active)
          : !Boolean(account?.active);

        if (!isActiveMatch) {
          return false;
        }

        if (!query) {
          return true;
        }

        const haystack = [
          account?.name,
          account?.owner,
          account?.bank,
          account?.maskedNumber,
          account?.branch,
          account?.code,
        ]
          .map((value) => String(value || '').toLowerCase())
          .join(' ');

        return haystack.includes(query);
      });
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredAccounts.length / this.perPage));
    },
    paginatedAccounts() {
      const offset = (this.currentPage - 1) * this.perPage;
      return this.filteredAccounts.slice(offset, offset + this.perPage);
    },
  },
  watch: {
    filteredAccounts() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  emits: ['movements', 'edit-account', 'transfer-account', 'toggle-account-status', 'open-account'],
  methods: {
    handleSearchChange(value) {
      this.search = String(value || '').trim();
      this.currentPage = 1;
    },
    resolveViewModeStorageKey() {
      let user = {};

      try {
        const raw = localStorage.getItem('user');
        user = raw ? (JSON.parse(raw) || {}) : {};
      } catch (error) {
        user = {};
      }

      const userId = String(user?.id || user?.user_id || user?.email || 'anonymous').trim() || 'anonymous';
      return `${BANK_ACCOUNTS_VIEW_MODE_STORAGE_PREFIX}.${userId}`;
    },
    resolveAccountHref(account) {
      const accountId = Number(account?.id || 0);
      if (accountId <= 0 || !this.$router) {
        return '#';
      }

      return this.$router.resolve({
        name: 'financialAccountDetail',
        params: { financialAccountId: String(accountId) },
        query: { tab: 'summary' },
      }).href;
    },
    restoreViewModeFromStorage() {
      try {
        const stored = String(localStorage.getItem(this.resolveViewModeStorageKey()) || '').trim();
        if (stored === 'cards' || stored === 'table') {
          this.viewMode = stored;
        }
      } catch (error) {
      }
    },
    persistViewModeInStorage(mode) {
      try {
        localStorage.setItem(this.resolveViewModeStorageKey(), mode);
      } catch (error) {
      }
    },
    handleStatusFilter(nextStatus) {
      this.statusFilter = String(nextStatus || 'active');
      this.currentPage = 1;
    },
    handleViewMode(mode) {
      const normalizedMode = mode === 'table' ? 'table' : 'cards';
      this.viewMode = normalizedMode;
      this.persistViewModeInStorage(normalizedMode);
      this.currentPage = 1;
    },
    handlePageChange(page) {
      this.currentPage = Number(page || 1);
    },
    handleMovements(account) {
      this.$emit('movements', account);
    },
    handleOpenAccount(account) {
      this.$emit('open-account', account);
    },
    handleEditAccount(account) {
      this.$emit('edit-account', account);
    },
    handleTransferAccount(account) {
      this.$emit('transfer-account', account);
    },
    handleToggleAccountStatus(account) {
      this.$emit('toggle-account-status', account);
    },
  },
};
</script>

<style scoped>
.financial-bank-accounts {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.financial-bank-accounts__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.financial-bank-accounts__table-wrap {
  overflow: hidden;
}

.financial-bank-accounts__table thead th {
  font-size: 12px;
  letter-spacing: 0.02em;
}

.financial-bank-accounts__table tbody td {
  vertical-align: middle;
  white-space: normal;
  word-break: break-word;
}

.financial-bank-accounts__cell-text {
  color: #334155;
  display: block;
  white-space: normal;
  line-height: 1.3;
}

.financial-table-account-cell {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  white-space: normal;
  max-width: 100%;
}

.financial-table-account-cell > span:last-child {
  display: inline-flex;
  flex-direction: column;
  min-width: 0;
}

.financial-table-account-cell .app-strong,
.financial-table-account-cell .app-muted {
  display: block;
  white-space: normal;
  line-height: 1.25;
}

.financial-table-account-link {
  text-decoration: none;
  color: inherit;
}

.financial-table-account-link:hover .app-strong {
  color: #0b63b4;
  text-decoration: underline;
}

.financial-table-account-cell__badge {
  min-width: 32px;
  height: 24px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
}

.financial-table-account-cell__badge.is-san {
  background: #dc2626;
}

.financial-table-account-cell__badge.is-usd {
  background: #7f1d1d;
}

.financial-table-account-cell__badge.is-cc {
  background: #f59e0b;
}

.financial-table-account-cell__badge.is-bb {
  background: #2563eb;
}

.financial-table-account-cell__badge.is-sp {
  background: #7c3aed;
}

.financial-table-account-cell__badge.is-sv {
  background: #0f766e;
}

.financial-account-empty {
  grid-column: 1 / -1;
}

.financial-account-empty p {
  margin: 0;
  color: #64748b;
}

.financial-bank-accounts__pagination {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 1200px) {
  .financial-bank-accounts__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .financial-bank-accounts__grid {
    grid-template-columns: 1fr;
  }

  .financial-account-card__footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
